import { useRouter } from 'expo-router';
import type { SQLiteDatabase } from 'expo-sqlite';
import { Check, ChevronRight, Plus, X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { GoalRow, TaskRow } from '@/db/types';
import { parseLinkedTaskIds, updateGoal } from '@/lib/goals';
import { addCustomTask, getTaskById } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

/**
 * Step tracker on the goal edit screen. Each step is a real task in the
 * `task` table — that way it shows up in the schedule and counts toward
 * "done this week" stats. The link is stored on the goal row as a JSON
 * array of task ids; small lists per goal so the JSON column is fine.
 *
 * Quick-add inline form (title + optional due date) creates a one-off
 * custom task scoped to the goal's property. Tap a step → open the task
 * detail. Remove unlinks from the goal but leaves the task — call sites
 * that want a true delete should use the task detail screen.
 */
export function GoalSteps({
  db,
  goal,
  onChange,
}: {
  db: SQLiteDatabase;
  goal: GoalRow;
  onChange: () => void;
}) {
  const router = useRouter();
  const [steps, setSteps] = useState<TaskRow[]>([]);
  const [newStep, setNewStep] = useState('');
  const [adding, setAdding] = useState(false);

  const linkedIds = parseLinkedTaskIds(goal.linked_task_ids_json);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rows: TaskRow[] = [];
      for (const id of linkedIds) {
        const t = await getTaskById(db, id);
        if (t) rows.push(t);
      }
      if (!cancelled) setSteps(rows);
    })();
    return () => { cancelled = true; };
    // Re-fetch when the linked id list changes.
  }, [db, goal.linked_task_ids_json]);

  const addStep = async () => {
    const title = newStep.trim();
    if (!title || adding) return;
    setAdding(true);
    try {
      // Default due: 14 days out, or 60 days before the goal target date
      // when one is set (so steps queue up ahead of the goal).
      const today = Date.now();
      const dueDefault = goal.target_date
        ? Math.max(today + 7 * 86_400_000, goal.target_date - 60 * 86_400_000)
        : today + 14 * 86_400_000;
      const taskId = await addCustomTask(db, {
        propertyId: goal.property_id,
        systemId: null,
        title,
        why: `Step toward: ${goal.title}`,
        icon: 'Target',
        tier: null,
        cadence_days: null,
        due_at: dueDefault,
        difficulty: 'easy',
        time_minutes: null,
        supplies: [],
        steps: [],
      });
      const nextIds = [...linkedIds, taskId];
      await updateGoal(db, goal.id, { linked_task_ids: nextIds });
      setNewStep('');
      onChange();
    } finally {
      setAdding(false);
    }
  };

  const unlinkStep = async (taskId: string) => {
    const nextIds = linkedIds.filter((id) => id !== taskId);
    await updateGoal(db, goal.id, { linked_task_ids: nextIds });
    onChange();
  };

  return (
    <View style={styles.card}>
      <Text style={styles.cardTitle}>STEPS · {steps.length}</Text>
      <Text style={styles.cardSub}>
        Break the goal into small, scheduled tasks. Each one shows up in your
        schedule like any other.
      </Text>

      {steps.length === 0 ? (
        <Text style={styles.empty}>
          No steps yet. Add the first concrete action below — &ldquo;get 3
          quotes,&rdquo; &ldquo;measure the deck.&rdquo;
        </Text>
      ) : (
        <View style={{ marginTop: 10, gap: 6 }}>
          {steps.map((s) => {
            const done = s.completed_at != null;
            return (
              <View key={s.id} style={[styles.row, done && { opacity: 0.55 }]}>
                <Pressable
                  onPress={() => router.push(`/task/${s.id}` as never)}
                  style={({ pressed }) => [styles.rowBody, pressed && { opacity: 0.85 }]}>
                  <View style={[styles.checkBox, done && styles.checkBoxDone]}>
                    {done ? <Check size={11} color={Palette.cream} strokeWidth={3} /> : null}
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      style={[styles.rowTitle, done && { textDecorationLine: 'line-through' }]}
                      numberOfLines={2}>
                      {s.title}
                    </Text>
                    {s.due_at ? (
                      <Text style={styles.rowMeta}>
                        Due {new Date(s.due_at).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Text>
                    ) : null}
                  </View>
                  <ChevronRight size={14} color={Palette.inkFaint} strokeWidth={1.7} />
                </Pressable>
                <Pressable
                  onPress={() => unlinkStep(s.id)}
                  hitSlop={8}
                  style={({ pressed }) => [styles.unlink, pressed && { opacity: 0.7 }]}
                  accessibilityLabel="Unlink step">
                  <X size={14} color={Palette.inkFaint} strokeWidth={2} />
                </Pressable>
              </View>
            );
          })}
        </View>
      )}

      <View style={styles.addRow}>
        <TextInput
          value={newStep}
          onChangeText={setNewStep}
          onSubmitEditing={addStep}
          placeholder="Add a step…"
          placeholderTextColor={Palette.inkFaint}
          returnKeyType="done"
          style={[styles.addInput, webNoOutline]}
        />
        <Pressable
          onPress={addStep}
          disabled={!newStep.trim() || adding}
          style={({ pressed }) => [
            styles.addBtn,
            (!newStep.trim() || adding) && { opacity: 0.4 },
            pressed && newStep.trim() && { transform: [{ scale: 0.96 }] },
          ]}>
          <Plus size={16} color={Palette.cream} strokeWidth={2.4} />
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  cardTitle: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.6,
  },
  cardSub: {
    marginTop: 4, fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    lineHeight: 17,
  },
  empty: {
    marginTop: 12, fontFamily: Fonts.sansMedium, fontSize: 13.5,
    color: Palette.inkFaint, lineHeight: 18,
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingVertical: 8, paddingHorizontal: 10,
    backgroundColor: Palette.sand,
    borderRadius: 11,
    borderWidth: 1, borderColor: Palette.line,
  },
  rowBody: {
    flex: 1,
    flexDirection: 'row', alignItems: 'center', gap: 10, minWidth: 0,
  },
  checkBox: {
    width: 18, height: 18, borderRadius: 5,
    borderWidth: 1.5, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: Palette.card,
  },
  checkBoxDone: { backgroundColor: Palette.moss, borderColor: Palette.moss },
  rowTitle: {
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink, lineHeight: 18,
  },
  rowMeta: {
    marginTop: 1, fontFamily: Fonts.sansMedium, fontSize: 12, color: Palette.inkSoft,
  },
  unlink: {
    width: 22, height: 22, borderRadius: 11,
    alignItems: 'center', justifyContent: 'center',
  },
  addRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    marginTop: 10,
  },
  addInput: {
    flex: 1,
    borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 10,
    fontFamily: Fonts.sansSemibold, fontSize: 13.5, color: Palette.ink,
  },
  addBtn: {
    width: 38, height: 38, borderRadius: 12,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
});
